import { Form, Input, message, Modal, Select } from "antd";
import {
  MeterType,
  usePositionConfigurationByPositionIdsQuery,
  useRemapMeterMutation,
  useUpdatePositionConfigurationMutation,
} from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useEffect, useState } from "react";
import { emonSettings } from "../InfrastructureDetails/MappedNodes/MappedNodes.d";

interface EditMeterModalProps {
  visible: boolean;
  onClose: () => void;
  locationId: string;
  meterData?: {
    meteringPointPositionId: string;
    positionName: string;
    meterType: MeterType;
    identifier: string; // nodeMacId for CT, serialNumber for camera
    currentCtClampType?: number;
    streamMappings?: Array<{
      subjectId: string;
      channelIndex: number;
    }>;
    // Add raw position data for additional meter type checking
    rawPositionData?: any;
  } | null;
  refetch: () => void;
}

const EditMeterModal: React.FC<EditMeterModalProps> = ({ visible, onClose, locationId, meterData, refetch }) => {
  const [editForm] = Form.useForm();
  const [initialValues, setInitialValues] = useState<any>({});

  const [remapMeter, { loading: remappingMeter }] = useRemapMeterMutation();
  const [updatePositionConfiguration, { loading: updatingPositionConfig }] = useUpdatePositionConfigurationMutation();

  // Set initial form values when modal opens or meterData changes (before CT clamp type is fetched)
  useEffect(() => {
    if (visible && meterData) {
      const values = {
        name: meterData.positionName,
        ctClampType: meterData.currentCtClampType,
      };
      setInitialValues(values);
      editForm.setFieldsValue(values);
    }
  }, [visible, meterData, editForm]);

  const resetModal = () => {
    editForm.resetFields();
    setInitialValues({});
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  // More explicit checking for CT meter - ensure it's NOT a camera and IS a CT meter
  const isCTMeter = meterData?.meterType === MeterType.Ct;
  const isCameraMeter = meterData?.meterType === MeterType.Camera;

  // Extract complex expressions to separate variables for static checking
  const meterIdentifier = meterData?.identifier;
  const meterRawPositionData = meterData?.rawPositionData;

  // Additional check using MAC ID pattern - CT meters start with FFFF, camera meters don't
  const isDefinitelyCameraMeter = React.useMemo(() => {
    if (isCameraMeter) return true;

    // Check if the identifier (MAC ID) doesn't start with FFFF - this indicates a camera meter
    // CT meters have MAC IDs starting with FFFF, camera meters use different patterns
    if (meterIdentifier && !meterIdentifier.toUpperCase().startsWith("FFFF")) {
      return true;
    }

    // Fallback: Check raw position data for subType: 'camera'
    if (meterRawPositionData?.nodeSlots) {
      const activeSlot = meterRawPositionData.nodeSlots.find((slot: any) => !slot.slotRemoveDate);
      const slot = activeSlot || meterRawPositionData.nodeSlots[0];
      if (slot?.subType === "camera") {
        return true;
      }
    }

    return false;
  }, [isCameraMeter, meterIdentifier, meterRawPositionData]);

  // Only show CT clamp field for CT meters that are NOT camera meters
  const shouldShowCtClampField = isCTMeter && !isDefinitelyCameraMeter;

  // Fetch position configuration to get current CT clamp type
  const { data: positionConfigData } = usePositionConfigurationByPositionIdsQuery({
    variables: {
      positionIds: meterData ? [meterData.meteringPointPositionId] : [],
    },
    skip: !meterData?.meteringPointPositionId || !shouldShowCtClampField,
  });

  // Extract complex expressions to separate variables for static checking
  const positionConfigurations = positionConfigData?.positionConfigurations;
  const meterCurrentCtClampType = meterData?.currentCtClampType;

  // Extract current CT clamp type from position configuration
  const currentCtClampType = React.useMemo(() => {
    if (positionConfigurations && positionConfigurations.length > 0) {
      return positionConfigurations[0].desiredEmonSwitchPosition;
    }
    return meterCurrentCtClampType;
  }, [positionConfigurations, meterCurrentCtClampType]);

  // Update initial values when CT clamp type is fetched
  React.useEffect(() => {
    if (visible && meterData && currentCtClampType !== undefined) {
      const values = {
        name: meterData.positionName,
        ctClampType: currentCtClampType,
      };
      setInitialValues(values);
      editForm.setFieldsValue(values);
    }
  }, [visible, meterData, editForm, currentCtClampType]);

  const handleSubmit = async () => {
    try {
      if (!meterData) {
        message.error("No meter data available for editing");
        return;
      }

      const values = await editForm.validateFields();
      const nameChanged = values.name !== initialValues.name;
      const ctClampChanged = values.ctClampType !== currentCtClampType;

      if (!nameChanged && !ctClampChanged) {
        message.info("No changes were made");
        handleClose();
        return;
      }

      // If name changed, use remapMeter to update position name
      if (nameChanged) {
        const { data: remapResult } = await remapMeter({
          variables: {
            locationId,
            positionName: values.name,
            meterType: meterData.meterType as any,
            identifier: meterData.identifier,
            streams: meterData.streamMappings || [],
          },
        });

        if (!remapResult?.remapMeter?.success) {
          throw new Error(remapResult?.remapMeter?.message || "Failed to update meter name");
        }

        message.success("Meter name updated successfully!");
      }

      // If CT clamp type changed (only for CT meters), update position configuration
      if (ctClampChanged && meterData.meterType === MeterType.Ct) {
        try {
          const positionConfigUpdate: any = {
            positionId: meterData.meteringPointPositionId,
          };

          // Add CT clamp type to desiredEmonSwitchPosition
          if (values.ctClampType !== undefined) {
            positionConfigUpdate.desiredEmonSwitchPosition = values.ctClampType;
          }

          await updatePositionConfiguration({
            variables: {
              positionConfiguration: positionConfigUpdate,
            },
          });

          message.success("CT clamp configuration updated successfully!");
        } catch (configError: any) {
          // Don't fail the whole operation if position config update fails
          message.warning(
            `Meter name updated successfully, but failed to update CT clamp configuration: ${configError.message}`
          );
        }
      }

      handleClose();
      await refetch();
    } catch (error: any) {
      message.error(error.message || "Failed to update meter");
    }
  };

  return (
    <Modal
      title="Edit Meter"
      open={visible}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Save Changes"
      cancelText="Cancel"
      confirmLoading={remappingMeter || updatingPositionConfig}
      width={500}
    >
      {meterData && (
        <>
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 6 }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>
              <strong>Meter Type:</strong>{" "}
              {(() => {
                if (isCameraMeter) return "Camera Meter";
                if (isCTMeter) return "CT Meter";
                return "Unknown";
              })()}
            </div>
            <div style={{ fontSize: 14, marginBottom: 8 }}>
              <strong>Identifier:</strong> {meterData.identifier}
            </div>
            <div style={{ fontSize: 14 }}>
              <strong>Current Name:</strong> {meterData.positionName}
            </div>
          </div>

          <Form form={editForm} layout="vertical">
            <Form.Item
              label="Meter Name"
              name="name"
              rules={[{ required: true, message: "Please enter a meter name" }]}
            >
              <Input placeholder="Enter meter name" />
            </Form.Item>

            {shouldShowCtClampField && (
              <Form.Item
                label="CT Clamp Type"
                name="ctClampType"
                tooltip="Select the type of CT clamp being used. This determines the current rating and configures the emon switch position for accurate measurements."
              >
                <Select
                  placeholder="Select CT clamp type (optional)"
                  allowClear
                  options={emonSettings.map((setting) => ({
                    label: setting.label,
                    value: setting.value,
                  }))}
                />
              </Form.Item>
            )}
          </Form>
        </>
      )}

      {!meterData && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <div style={{ color: "#999" }}>No meter data available for editing</div>
        </div>
      )}
    </Modal>
  );
};

export default EditMeterModal;
